import * as Clipboard from 'expo-clipboard';
import CarrotIcon from '@assets/icons/carrot.svg';
import i18n from '@/utils/i18n';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ActivityIndicator } from '@/components/activity-indicator';
import { adaptCreditRequestLineItemToBatchInput } from '@/hooks/use-create-credit-request';
import {
  AlertLevel,
  CreditMemoAmountInput,
  CreditMemoInput,
  InvoiceInput,
  MakePaymentInvoiceInput,
  OrderInvoiceAmountInput,
  PaymentBatchFeeInput,
  PaymentBatchLineItemName,
  Payments_MakePartialPaymentWithCreditRequestMutation,
  Payments_MakePartialPaymentWithCreditRequestMutationVariables,
  Payments_MakePaymentMutation,
  Payments_MakePaymentMutationVariables,
  TransactionFeesType,
} from '@/types';
import { ApolloError, gql, useMutation } from '@apollo/client';
import {
  APP_CONTAINER_ROUTES,
  APP_MODAL_ROUTES,
  PAYMENT_ROUTES,
  SHORT_PAY_CREDIT_REQUEST_ROUTES,
} from '@/navigators/routes';
import { AppModalStackParams, PaymentsStackParams } from '@/navigators/types';
import { Button } from '@/components/button';
import {
  calculateTransactionFeeInMicros,
  toAdyenPaymentJson,
} from '@/utils/payments';
import { Checkbox } from '@/components/checkbox';
import {
  CheckSubmitRecovery,
  useSubmitCheckDeposit,
} from '@/hooks/payments/use-submit-check-deposit';
import {
  CombinedPaymentsActionSheet,
  PaymentsActionSheetView,
} from './combined-payments-action-sheet';
import {
  CompositeNavigationProp,
  useIsFocused,
  useNavigation,
} from '@react-navigation/native';
import { createStyles } from '@/contexts/theme-context';
import { CreditRequestBatchLineItem } from '@/contexts/credit-request-context-types';
import { EmptyStateMinimal } from '@/components/empty-state-minimal';
import { FONTS, TYPOGRAPHY } from '@/styles';
import { formatCurrency } from '@/utils/money';
import {
  getBatchItems,
  getEffectiveAmountInMicros,
  isPartialActive,
  usePaymentFlowStore,
} from '@/stores/use-payment-flow-store';
import { getMessageFromGQLError } from '@/utils/apollo-client';
import { isKlarnaEnabledForSupplier } from './klarna-utils';
import { type KlarnaVariant } from '@/hooks/payments/klarna-types';
import { OfflineComponent } from '@/components/offline-component';
import { PayActionsButton } from '@/components/payments/pay-actions-button';
import {
  PayCapability,
  usePayCapability,
} from '@/hooks/payments/use-pay-capability';
import { PaymentItemType, PaymentMethodType, PaymentsStatus } from '@/enums';
import {
  PaymentMethodOption,
  UnpaidItemType,
  usePaymentsContext,
} from '@/contexts/payments-context';
import { PaymentReviewActionSheet } from './payment-review-action-sheet';
import {
  PaymentSubmissionResult,
  TabCardDetails,
} from '@/components/payments/types';
import { PaymentSuccessActionSheet } from './payment-success-action-sheet';
import { Platform, Share, Text, View } from 'react-native';
import { Spacing } from '@usepepper/sprout-kit';
import { StackNavigationProp } from '@react-navigation/stack';
import { UnpaidInvoiceList } from './unpaid-invoice-list';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useCheckCaptureStore } from '@/stores/check-capture-store';
import { useCreditRequestInvoiceStoreApi } from '@/stores/credit-request-invoice-store';
import { useGeneratePaymentLink } from '@/hooks/payments/use-generate-payment-link';
import { useGetPaymentHistoryQuery } from '@/hooks/use-get-payment-history-query';
import { useInitiateFTNI } from '@/hooks/use-initiate-ftni';
import { useKlarnaNativeFlow } from '@/hooks/payments/use-klarna-native-flow';
import { useKlarnaPaymentMethods } from '@/hooks/payments/use-klarna-payment-methods';
import { useKlarnaStore, variantOf } from '@/stores/use-klarna-store';
import { useKlarnaWebFlow } from '@/hooks/payments/use-klarna-web-flow';
import { useLayoutContext } from '@/contexts/layout-context';
import { useOfflineModeActive } from '@/hooks/use-offline-mode-active';
import { usePaymentMethodStore } from '@/stores/use-payment-method-store';
import { useRecordCashPayment } from '@/hooks/payments/use-record-cash-payment';
import { useRecordCheckPayment } from '@/hooks/payments/use-record-check-payment';
import { useToasts } from '@/contexts/toast-context';

const PAYMENT_RESPONSE_FRAGMENT = gql`
  fragment PaymentResponseFields on MakePaymentResponse {
    alert_level
    alert_message
    payment_batch_id
  }
`;

export const MAKE_PAYMENT_MUTATION = gql`
  mutation Payments_MakePayment(
    $batches: [MakePaymentBatchInput!]!
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
  ) {
    makePayment(
      batches: $batches
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
    ) {
      ...PaymentResponseFields
    }
  }
  ${PAYMENT_RESPONSE_FRAGMENT}
`;

export const MAKE_PARTIAL_PAYMENT_WITH_CREDIT_REQUEST_MUTATION = gql`
  mutation Payments_MakePartialPaymentWithCreditRequest(
    $chatUUID: uuid!
    $creditRequestBatchLineItems: [CreditRequestBatchLineItemInput!]!
    $fees: [PaymentBatchFeeInput!]
    $invoices: [PartialPaymentInvoiceInput!]!
    $memo: String
    $paymentMethod: String!
    $restaurantUUID: uuid!
  ) {
    makePartialPaymentWithCreditRequest(
      chat_id: $chatUUID
      credit_request_batch_line_items: $creditRequestBatchLineItems
      fees: $fees
      invoices: $invoices
      memo: $memo
      payment_method: $paymentMethod
      restaurant_id: $restaurantUUID
    ) {
      ...PaymentResponseFields
    }
  }
  ${PAYMENT_RESPONSE_FRAGMENT}
`;

type NextPaymentStep = 'review' | 'wallet';

interface PayRoutingState {
  capability: PayCapability;
  cashSelected: boolean;
  checkSelected: boolean;
  hasSelectedPaymentMethod: boolean;
  isKlarnaSelected: boolean;
  isSmallLayout: boolean;
  manualCheckSelected: boolean;
}

const nextPaymentStep = (state: PayRoutingState): NextPaymentStep => {
  if (
    !state.isSmallLayout ||
    state.cashSelected ||
    state.checkSelected ||
    state.isKlarnaSelected ||
    state.manualCheckSelected
  ) {
    return 'review';
  }
  if (!state.capability.isDistributorUser && state.hasSelectedPaymentMethod) {
    return 'review';
  }
  return 'wallet';
};

interface Props {
  contentSpacing?: Spacing;
  onPaymentSuccessAutoPayEnrollment: () => void;
  onReviewBannerPress?: () => void;
  renderListItem: (args: {
    isChecked: boolean;
    onCheck: () => void;
    unpaidItem: UnpaidItemType;
  }) => React.ReactElement | null;
  reviewBanner?: (navigation: {
    jumpToTabCardApplication: () => void;
  }) => React.ReactNode;
  tabCardDetails?: TabCardDetails | null;
  walletFooterBanner?: (navigation: {
    jumpToTabCardApplication: () => void;
  }) => React.ReactNode;
}

export const UnpaidInvoiceComponent: React.FC<Props> = ({
  contentSpacing = Spacing.SPACE_MEDIUM,
  onPaymentSuccessAutoPayEnrollment,
  onReviewBannerPress,
  renderListItem,
  reviewBanner,
  tabCardDetails,
  walletFooterBanner,
}) => {
  const creditRequestStoreApi = useCreditRequestInvoiceStoreApi();
  const {
    allUnpaidItems,
    autoShowModal,
    currencyCode,
    deselectAllUnpaidItems,
    enabledPaymentMethods,
    ftniEnabled,
    loading: contextLoading,
    memo,
    paymentMethods,
    refreshInvoices: refreshUnpaid,
    refreshing,
    resetContext,
    selectAllUnpaidItems,
    selectedInvoiceUUIDs,
    selectedPaymentMethod,
    setPaymentContextState,
    isGrossUpTransactionFee,
    transactionFeeMap,
  } = usePaymentsContext();
  const { isSmallLayout } = useLayoutContext();
  const [styles, theme] = useStyles();
  const { refresh: refreshHistory } = useGetPaymentHistoryQuery();
  const {
    selectedRestaurantRelationship: {
      chatUUIDs,
      employeeRole: restaurantEmployeeRole,
      showCreditRequest,
      showPaymentLinks,
      showPaymentsTab,
      supplierPaymentsStatus,
      transactionFeeName,
      supplierUUID,
    },
    selectedRestaurantUUID: restaurantUUID,
    supplierEmployeeRole,
  } = useAuthenticatedUserContext();
  const { navigate } =
    useNavigation<
      CompositeNavigationProp<
        StackNavigationProp<PaymentsStackParams, PAYMENT_ROUTES>,
        StackNavigationProp<AppModalStackParams>
      >
    >();
  const { showErrorToast, showToast } = useToasts();
  const isFocused = useIsFocused();
  const isOfflineModeActive = useOfflineModeActive();
  const capability = usePayCapability({
    enabledPaymentMethods,
    restaurantEmployeeRole,
    showPaymentLinks,
    supplierEmployeeRole,
  });
  const [generatePaymentLink, { loading: isGeneratingPaymentLink }] =
    useGeneratePaymentLink();

  const [showPaymentReviewActionSheet, setShowPaymentReviewActionSheet] =
    useState(false);
  const [showPaymentSuccessModal, setShowPaymentSuccessModal] = useState(false);
  // Cash / manual-check selection lives in its own store (not local state) so
  // it survives the Android screen remount between the wallet and the review
  // sheet.
  const cashSelected = usePaymentMethodStore((s) => s.cashSelected);
  const manualCheckSelected = usePaymentMethodStore(
    (s) => s.manualCheckSelected,
  );
  const checkNumber = usePaymentMethodStore((s) => s.checkNumber);
  const setCheckNumber = usePaymentMethodStore((s) => s.setCheckNumber);
  const paymentFlow = usePaymentFlowStore((s) => s.flow);

  const isKlarnaEnabled =
    !capability.isDistributorUser &&
    !isPartialActive(paymentFlow) &&
    isKlarnaEnabledForSupplier(supplierUUID);
  const { klarnaPaymentMethods, loading: klarnaLoading } =
    useKlarnaPaymentMethods({
      isKlarnaEnabled,
      restaurantUUID,
      supplierUUID,
    });
  const klarnaSelection = useKlarnaStore((s) => s.selection);
  const klarnaVariant = variantOf(klarnaSelection);
  const clearKlarnaSelection = useKlarnaStore((s) => s.clearKlarnaSelection);
  const chooseKlarnaVariant = useKlarnaStore((s) => s.chooseKlarnaVariant);

  const loading = contextLoading || klarnaLoading;

  const [initialActionSheetView, setInitialActionSheetView] = useState<
    PaymentsActionSheetView | undefined
  >(undefined);

  const resetCheckCaptureStore = useCheckCaptureStore((s) => s.reset);
  const clearCheckSelection = useCheckCaptureStore(
    (s) => s.clearCheckSelection,
  );
  const checkSelected = useCheckCaptureStore((s) => s.submission.checkSelected);
  const checkCredentials = useCheckCaptureStore((s) => s.credentials);
  const [submitCheckDeposit, { loading: submittingCheckDeposit }] =
    useSubmitCheckDeposit();

  const cashActive = cashSelected && !selectedPaymentMethod && !checkSelected;
  const manualCheckActive =
    manualCheckSelected && !selectedPaymentMethod && !checkSelected;

  const [makePayment, { loading: makePaymentLoading }] = useMutation<
    Payments_MakePaymentMutation,
    Payments_MakePaymentMutationVariables
  >(MAKE_PAYMENT_MUTATION);

  const [
    makePartialPaymentWithCreditRequest,
    { loading: makePartialPaymentWithCreditRequestLoading },
  ] = useMutation<
    Payments_MakePartialPaymentWithCreditRequestMutation,
    Payments_MakePartialPaymentWithCreditRequestMutationVariables
  >(MAKE_PARTIAL_PAYMENT_WITH_CREDIT_REQUEST_MUTATION);

  const [recordCashPayment, { loading: recordCashPaymentLoading }] =
    useRecordCashPayment();

  const [recordCheckPayment, { loading: recordCheckPaymentLoading }] =
    useRecordCheckPayment();

  const isSubmittingPayment =
    makePaymentLoading ||
    makePartialPaymentWithCreditRequestLoading ||
    recordCashPaymentLoading ||
    recordCheckPaymentLoading ||
    submittingCheckDeposit;

  const subtotalInMicros = useMemo(() => {
    return (allUnpaidItems || []).reduce(
      (acc, inv) =>
        selectedInvoiceUUIDs.has(inv.uuid) ? acc + inv.amountInMicros : acc,
      0,
    );
  }, [allUnpaidItems, selectedInvoiceUUIDs]);

  const singleInvoiceSelected = useMemo(() => {
    if (selectedInvoiceUUIDs.size !== 1) {
      return false;
    }
    const [uuid] = Array.from(selectedInvoiceUUIDs);
    return allUnpaidItems.some(
      (item) => item.uuid === uuid && item.type === PaymentItemType.Invoice,
    );
  }, [allUnpaidItems, selectedInvoiceUUIDs]);

  /**
   * Payment amounts are derived from the current selection and whether a
   * partial payment flow is active and an amount has been set. The first
   * three are captured in the payment store; the transaction fee is
   * computed downstream from whichever amount is effective.
   *
   * Amounts are defined as follows:
   *
   * - `subtotalInMicros`: sum of currently-selected invoices.
   *
   * - `partialPaymentAmountInMicros`: either the amount entered in the
   *   partial-payment modal or the amount derived from the credit request
   *   workflow.
   *
   * - `effectiveSubtotalInMicros`: the actual amount being paid. Equals
   *   `partialPaymentAmountInMicros` when a partial flow is active on a single
   *   invoice; otherwise falls back to `subtotalInMicros` (the full amount).
   *
   * - `effectiveCalculatedTransactionFee`: the actual transaction fee being
   *   charged. Equals the fee on `partialPaymentAmountInMicros` when a partial
   *   flow is active on a single invoice; otherwise falls back to the fee on
   *   `subtotalInMicros`.
   */
  const partialPaymentAmountInMicros =
    paymentFlow.status === 'partialAmount' ||
    paymentFlow.status === 'creditRequest'
      ? paymentFlow.amountInMicros
      : null;

  const partialPaymentBatchItems = getBatchItems(paymentFlow);

  const partialPaymentActive =
    singleInvoiceSelected && isPartialActive(paymentFlow);

  const effectiveSubtotalInMicros = partialPaymentActive
    ? getEffectiveAmountInMicros(paymentFlow)
    : subtotalInMicros;

  const handleUseCheck = useCallback(() => {
    setPaymentContextState({ autoShowModal: true });
    navigate(PAYMENT_ROUTES.Base);
  }, [navigate, setPaymentContextState]);

  const resetShortPay = useCallback(() => {
    usePaymentFlowStore.getState().reset();
    usePaymentMethodStore.getState().reset();
    setInitialActionSheetView(undefined);
    creditRequestStoreApi.getState().clearBatchItems();
  }, [creditRequestStoreApi]);

  // Selecting "Record cash payment" from the wallet. Cash has no payment
  // method, so we clear any card/check selection and mark the flow as cash.
  const markCashPayment = useCallback(() => {
    clearCheckSelection();
    setPaymentContextState({ selectedPaymentMethod: undefined });
    usePaymentMethodStore.getState().selectCash();
  }, [clearCheckSelection, setPaymentContextState]);

  const onRecordCashPayment = useCallback(() => {
    markCashPayment();
    setPaymentContextState({ autoShowModal: true });
    navigate(PAYMENT_ROUTES.Base);
  }, [markCashPayment, navigate, setPaymentContextState]);

  const markCheckPayment = useCallback(() => {
    clearCheckSelection();
    setPaymentContextState({ selectedPaymentMethod: undefined });
    usePaymentMethodStore.getState().selectManualCheck();
  }, [clearCheckSelection, setPaymentContextState]);

  const onRecordCheckPayment = useCallback(() => {
    markCheckPayment();
    setPaymentContextState({ autoShowModal: true });
    navigate(PAYMENT_ROUTES.Base);
  }, [markCheckPayment, navigate, setPaymentContextState]);

  const handleCheckCaptureUnavailable = useCallback(
    (message: string) => {
      showErrorToast(message);
      resetShortPay();
      resetCheckCaptureStore();
      setPaymentContextState({ autoShowModal: false });
    },
    [
      resetCheckCaptureStore,
      resetShortPay,
      setPaymentContextState,
      showErrorToast,
    ],
  );

  const onPayByCheck = useCallback(() => {
    clearKlarnaSelection();
    const store = usePaymentFlowStore.getState();
    navigate(APP_MODAL_ROUTES.CheckCapture, {
      chatUUID: chatUUIDs[0],
      currencyCode,
      onUnavailable: handleCheckCaptureUnavailable,
      onUseCheck: handleUseCheck,
      subtotalInMicros: getEffectiveAmountInMicros(store.flow),
    });
  }, [
    chatUUIDs,
    clearKlarnaSelection,
    currencyCode,
    handleCheckCaptureUnavailable,
    handleUseCheck,
    navigate,
  ]);

  const onSelectKlarna = useCallback(
    (variant: KlarnaVariant) => {
      clearCheckSelection();
      chooseKlarnaVariant({ variant });
    },
    [chooseKlarnaVariant, clearCheckSelection],
  );

  const [isKlarnaBusy, setIsKlarnaBusy] = useState(false);

  const resetKlarnaPaymentState = useCallback(() => {
    usePaymentFlowStore.getState().reset();
    resetCheckCaptureStore();
    creditRequestStoreApi.getState().clearBatchItems();
    clearKlarnaSelection();

    resetContext();
    setPaymentContextState({ autoShowModal: false });
  }, [
    clearKlarnaSelection,
    creditRequestStoreApi,
    resetCheckCaptureStore,
    resetContext,
    setPaymentContextState,
  ]);

  const onKlarnaSuccess = useCallback(() => {
    resetKlarnaPaymentState();
    // On large, the combined sheet transitions to Success itself.
    if (isSmallLayout) {
      setShowPaymentReviewActionSheet(false);
      setShowPaymentSuccessModal(true);
    }
  }, [isSmallLayout, resetKlarnaPaymentState]);

  const refreshQueries = useCallback(() => {
    refreshHistory();
    refreshUnpaid();
  }, [refreshHistory, refreshUnpaid]);

  const { startFlow: runKlarnaWebFlow } = useKlarnaWebFlow({
    onKlarnaPayment: onKlarnaSuccess,
    refreshQueries,
    restaurantUUID,
    supplierUUID,
  });
  const { startFlow: runKlarnaNativeFlow } = useKlarnaNativeFlow({
    onKlarnaPayment: onKlarnaSuccess,
    refreshQueries,
    restaurantUUID,
    supplierUUID,
  });

  const onKlarnaContinue = useCallback(async (): Promise<boolean> => {
    if (!klarnaVariant || !klarnaPaymentMethods.includes(klarnaVariant)) {
      return false;
    }

    const subtotal = getEffectiveAmountInMicros(
      usePaymentFlowStore.getState().flow,
    );
    const invoices: InvoiceInput[] = [];
    const creditMemos: CreditMemoInput[] = [];
    (allUnpaidItems || []).forEach((item) => {
      if (
        !selectedInvoiceUUIDs.has(item.uuid) ||
        typeof item.amountInMicros !== 'number'
      ) {
        return;
      }
      if (item.type === PaymentItemType.Invoice) {
        invoices.push({
          amount_in_micros: item.amountInMicros,
          invoice_id: item.uuid,
          invoice_number: item.displayNumber,
          restaurant_id: restaurantUUID,
        });
      } else if (item.type === PaymentItemType.Credit) {
        creditMemos.push({
          amount_in_micros: Math.abs(item.amountInMicros),
          credit_memo_id: item.uuid,
          credit_memo_number: item.displayNumber,
          restaurant_id: restaurantUUID,
        });
      }
    });
    const feesInMicros = calculateTransactionFeeInMicros(
      transactionFeeMap,
      klarnaVariant,
      subtotal,
      isGrossUpTransactionFee,
    );

    setIsKlarnaBusy(true);
    try {
      const runFlow =
        Platform.OS === 'web' ? runKlarnaWebFlow : runKlarnaNativeFlow;
      return await runFlow({ creditMemos, feesInMicros, invoices });
    } catch {
      showErrorToast();
      return false;
    } finally {
      setIsKlarnaBusy(false);
    }
  }, [
    allUnpaidItems,
    isGrossUpTransactionFee,
    klarnaPaymentMethods,
    klarnaVariant,
    restaurantUUID,
    runKlarnaNativeFlow,
    runKlarnaWebFlow,
    selectedInvoiceUUIDs,
    showErrorToast,
    transactionFeeMap,
  ]);

  const proceedToPay = useCallback(() => {
    const nextStep = nextPaymentStep({
      capability,
      cashSelected: cashActive,
      checkSelected,
      hasSelectedPaymentMethod: !!selectedPaymentMethod,
      isKlarnaSelected: klarnaVariant !== null,
      isSmallLayout,
      manualCheckSelected: manualCheckActive,
    });
    if (nextStep === 'review') {
      setShowPaymentReviewActionSheet(true);
      return;
    }
    setPaymentContextState({ autoShowModal: true });
    if (
      capability.walletMethods.has(PaymentMethodType.PAD) ||
      capability.canPayByCheck ||
      capability.canRecordCashPayment ||
      capability.canRecordCheckPayment ||
      klarnaPaymentMethods.length > 0
    ) {
      navigate(PAYMENT_ROUTES.PaymentMethod, {
        isDistributorUser: capability.isDistributorUser,
        klarnaPaymentMethods,
        onAddPADPaymentMethod: () => {
          navigate(PAYMENT_ROUTES.Base);
        },
        onBeforeBackPress: () => {
          resetShortPay();
          resetCheckCaptureStore();
          setPaymentContextState({ autoShowModal: false });
        },
        onPayByCheck: capability.canPayByCheck ? onPayByCheck : undefined,
        onRecordCashPayment: capability.canRecordCashPayment
          ? onRecordCashPayment
          : undefined,
        onRecordCheckPayment: capability.canRecordCheckPayment
          ? onRecordCheckPayment
          : undefined,
        onSelectKlarna: klarnaPaymentMethods.length
          ? onSelectKlarna
          : undefined,
        requireSavedPaymentMethod: capability.walletMethods.size > 0,
      });
    } else {
      navigate(APP_MODAL_ROUTES.AddPaymentMethod, {
        onAddUnsavedPaymentMethod: (paymentJson) => {
          navigate(PAYMENT_ROUTES.Base, {
            unsavedPaymentMethodJson: paymentJson,
          });
        },
        // Prevent re-navigation on modal container close
        onBackPress: () => {
          resetShortPay();
          resetCheckCaptureStore();
          setPaymentContextState({ autoShowModal: false });
        },
        onSavePaymentMethod: () => {
          // Allows review action sheet to open in UnpaidInvoiceComponent
          setPaymentContextState({ autoShowModal: true });
          navigate(PAYMENT_ROUTES.Base);
        },
        transactionFeeMap,
      });
    }
  }, [
    capability,
    cashActive,
    manualCheckActive,
    checkSelected,
    isSmallLayout,
    klarnaPaymentMethods,
    klarnaVariant,
    navigate,
    onPayByCheck,
    onRecordCashPayment,
    onRecordCheckPayment,
    onSelectKlarna,
    resetCheckCaptureStore,
    resetShortPay,
    selectedPaymentMethod,
    setPaymentContextState,
    transactionFeeMap,
  ]);

  useEffect(() => {
    if (autoShowModal && isFocused) {
      setShowPaymentReviewActionSheet(true);
    }
  }, [autoShowModal, isFocused]);

  useEffect(() => {
    if (
      supplierPaymentsStatus === PaymentsStatus.Enabled &&
      showPaymentsTab &&
      showPaymentReviewActionSheet &&
      isSmallLayout &&
      !loading &&
      !cashActive &&
      !manualCheckActive &&
      !(
        paymentMethods.length ||
        selectedPaymentMethod ||
        checkSelected ||
        klarnaVariant
      )
    ) {
      proceedToPay();
      setShowPaymentReviewActionSheet(false);
    }
  }, [
    cashActive,
    manualCheckActive,
    checkSelected,
    isSmallLayout,
    klarnaVariant,
    loading,
    paymentMethods.length,
    proceedToPay,
    selectedPaymentMethod,
    showPaymentReviewActionSheet,
    showPaymentsTab,
    supplierPaymentsStatus,
  ]);

  const allUnpaidItemsSelected =
    selectedInvoiceUUIDs.size === allUnpaidItems?.length;

  const handleCreditRequestWorkflowComplete = useCallback(
    (
      paymentAmountInMicros: number,
      batchItems: CreditRequestBatchLineItem[],
    ) => {
      const store = usePaymentFlowStore.getState();
      store.updateCreditRequestFlow({
        amountInMicros: paymentAmountInMicros,
        batchItems,
        subtotalInMicros,
      });
      setPaymentContextState({ autoShowModal: true });
      proceedToPay();
    },
    [proceedToPay, setPaymentContextState, subtotalInMicros],
  );

  const toggleInvoiceSelection = (uuid: string) => {
    const invoices = new Set(selectedInvoiceUUIDs);
    if (invoices.has(uuid)) {
      invoices.delete(uuid);
    } else {
      invoices.add(uuid);
    }
    setPaymentContextState({ selectedInvoiceUUIDs: invoices });
  };

  const computeTransactionFee = useCallback(
    (amountInMicros: number, overrideMethodType?: PaymentMethodType) => {
      if (checkSelected || cashActive || manualCheckActive) {
        // Transaction fees are not supported for remote deposit or manually
        // recorded cash / check payments.
        return 0;
      }
      const methodType = overrideMethodType ?? selectedPaymentMethod?.type;
      if (!methodType) {
        return 0;
      }
      return calculateTransactionFeeInMicros(
        transactionFeeMap,
        methodType,
        amountInMicros,
        isGrossUpTransactionFee,
      );
    },
    [
      cashActive,
      manualCheckActive,
      checkSelected,
      isGrossUpTransactionFee,
      selectedPaymentMethod,
      transactionFeeMap,
    ],
  );

  const effectiveCalculatedTransactionFee = useMemo(
    () =>
      computeTransactionFee(
        effectiveSubtotalInMicros,
        klarnaVariant ?? undefined,
      ),
    [computeTransactionFee, effectiveSubtotalInMicros, klarnaVariant],
  );
  const payAmountInMicrosOverride = partialPaymentActive
    ? effectiveSubtotalInMicros + effectiveCalculatedTransactionFee
    : null;

  const finalInvoices = useMemo(
    () =>
      allUnpaidItems.reduce<MakePaymentInvoiceInput[]>((acc, unpaidItem) => {
        if (
          selectedInvoiceUUIDs.has(unpaidItem.uuid) &&
          typeof unpaidItem.amountInMicros === 'number'
        ) {
          const amountInMicros = partialPaymentActive
            ? effectiveSubtotalInMicros
            : unpaidItem.amountInMicros!;
          acc.push({
            amount_in_micros_string: String(amountInMicros),
            due_at:
              (unpaidItem.type === PaymentItemType.Invoice &&
                unpaidItem.dueAt?.toISOString()) ||
              null,
            invoice_id: unpaidItem.uuid,
            invoice_number: unpaidItem.displayNumber,
          });
        }

        return acc;
      }, []),
    [
      allUnpaidItems,
      effectiveSubtotalInMicros,
      partialPaymentActive,
      selectedInvoiceUUIDs,
    ],
  );

  const selectedOrderInvoices = useMemo(() => {
    const uuids: string[] = [];
    const numbers: string[] = [];

    allUnpaidItems.forEach((item) => {
      if (
        selectedInvoiceUUIDs.has(item.uuid) &&
        item.type === PaymentItemType.Invoice
      ) {
        uuids.push(item.uuid);
        numbers.push(item.displayNumber);
      }
    });

    return { numbers, uuids };
  }, [allUnpaidItems, selectedInvoiceUUIDs]);

  const selectedOrderInvoiceUUIDs = selectedOrderInvoices.uuids;
  const selectedOrderInvoiceNumbers = selectedOrderInvoices.numbers;

  const selectedOrderInvoiceAmounts: OrderInvoiceAmountInput[] = useMemo(() => {
    return allUnpaidItems
      .filter(
        (item) =>
          selectedInvoiceUUIDs.has(item.uuid) &&
          item.type === PaymentItemType.Invoice,
      )
      .map((item) => ({
        amount_in_micros: item.amountInMicros!,
        order_invoice_id: item.uuid,
      }));
  }, [allUnpaidItems, selectedInvoiceUUIDs]);

  const selectedCreditMemoAmounts: CreditMemoAmountInput[] = useMemo(() => {
    return allUnpaidItems
      .filter(
        (item) =>
          selectedInvoiceUUIDs.has(item.uuid) &&
          item.type === PaymentItemType.Credit,
      )
      .map((item) => ({
        amount_in_micros: -item.amountInMicros!,
        credit_memo_id: item.uuid,
      }));
  }, [allUnpaidItems, selectedInvoiceUUIDs]);

  const submitPayment: () => Promise<PaymentSubmissionResult> =
    useCallback(async () => {
      if (!finalInvoices.length) {
        return;
      }

      if (cashActive) {
        const response = await recordCashPayment({
          variables: {
            creditMemos: selectedCreditMemoAmounts.map((creditMemo) => ({
              credit_memo_id: creditMemo.credit_memo_id,
            })),
            creditRequestBatchLineItems: partialPaymentBatchItems.length
              ? adaptCreditRequestLineItemToBatchInput(partialPaymentBatchItems)
              : [],
            invoices: finalInvoices
              .filter(
                (inv) =>
                  inv.invoice_id &&
                  selectedOrderInvoiceUUIDs.includes(inv.invoice_id),
              )
              .map((inv) => ({
                amount_in_micros_string: inv.amount_in_micros_string,
                order_invoice_id: inv.invoice_id!,
              })),
            memo,
            restaurantUUID,
            supplierUUID,
          },
        });
        return response.data?.recordCashPayment;
      }

      if (manualCheckActive) {
        const response = await recordCheckPayment({
          variables: {
            checkNumber: checkNumber.trim(),
            creditMemos: selectedCreditMemoAmounts.map((creditMemo) => ({
              credit_memo_id: creditMemo.credit_memo_id,
            })),
            creditRequestBatchLineItems: partialPaymentBatchItems.length
              ? adaptCreditRequestLineItemToBatchInput(partialPaymentBatchItems)
              : [],
            invoices: finalInvoices
              .filter(
                (inv) =>
                  inv.invoice_id &&
                  selectedOrderInvoiceUUIDs.includes(inv.invoice_id),
              )
              .map((inv) => ({
                amount_in_micros_string: inv.amount_in_micros_string,
                order_invoice_id: inv.invoice_id!,
              })),
            memo,
            restaurantUUID,
            supplierUUID,
          },
        });
        return response.data?.recordCheckPayment;
      }

      if (!selectedPaymentMethod) {
        return;
      }

      const fees: PaymentBatchFeeInput[] | null =
        effectiveCalculatedTransactionFee > 0
          ? [
              {
                amount_in_micros_string: String(
                  effectiveCalculatedTransactionFee,
                ),
                name: PaymentBatchLineItemName.TransactionFee,
                type: transactionFeeMap.FLAT[selectedPaymentMethod.type]
                  ? TransactionFeesType.Flat
                  : TransactionFeesType.Percentage,
              },
            ]
          : null;
      const paymentMethod = selectedPaymentMethod.isUnsaved
        ? selectedPaymentMethod.adyenPaymentMethodJSON!
        : toAdyenPaymentJson(selectedPaymentMethod);

      if (partialPaymentBatchItems.length > 0) {
        const response = await makePartialPaymentWithCreditRequest({
          variables: {
            chatUUID: chatUUIDs[0],
            creditRequestBatchLineItems: adaptCreditRequestLineItemToBatchInput(
              partialPaymentBatchItems,
            ),
            fees,
            invoices: finalInvoices.map((inv) => ({
              amount_in_micros_string: inv.amount_in_micros_string,
              order_invoice_id: inv.invoice_id!,
            })),
            memo,
            paymentMethod,
            restaurantUUID,
          },
        });
        return response.data?.makePartialPaymentWithCreditRequest;
      }

      const response = await makePayment({
        variables: {
          batches: [
            {
              credit_amount_in_micros_string: null,
              fees,
              invoices: finalInvoices,
              memo,
              payment_method: paymentMethod,
              restaurant_id: restaurantUUID,
            },
          ],
          restaurantUUID,
          supplierUUID,
        },
      });
      return response.data?.makePayment[0]!;
    }, [
      cashActive,
      checkNumber,
      manualCheckActive,
      chatUUIDs,
      effectiveCalculatedTransactionFee,
      finalInvoices,
      makePartialPaymentWithCreditRequest,
      makePayment,
      memo,
      partialPaymentBatchItems,
      recordCashPayment,
      recordCheckPayment,
      restaurantUUID,
      selectedCreditMemoAmounts,
      selectedOrderInvoiceUUIDs,
      selectedPaymentMethod,
      supplierUUID,
      transactionFeeMap,
    ]);

  const showSelectAllCheckbox =
    capability.canMakePayment || capability.canShareLink;
  const showPayInvoicesButton =
    selectedInvoiceUUIDs.size > 0 && capability.canMakePayment;
  const showPaymentLinksButton =
    selectedInvoiceUUIDs.size > 0 && capability.canShareLink;
  const showPayPartialButton = capability.canShortPay && singleInvoiceSelected;
  const showPayActionsButton =
    showPaymentLinksButton && showPayInvoicesButton && showPayPartialButton;

  const showSmallPaymentReviewActionSheet =
    showPaymentReviewActionSheet &&
    isSmallLayout &&
    (Boolean(selectedPaymentMethod) ||
      checkSelected ||
      cashActive ||
      Boolean(klarnaVariant) ||
      manualCheckActive);

  // Setup FTNI payments for distributors using it
  const initiateFTNI = useInitiateFTNI();
  const onHandleInitiateFTNI = useCallback(async () => {
    if (selectedInvoiceUUIDs) {
      try {
        await initiateFTNI(
          selectedOrderInvoiceNumbers,
          selectedOrderInvoiceUUIDs,
        );
      } catch (e) {
        console.error(e);
      }
    }
  }, [
    initiateFTNI,
    selectedInvoiceUUIDs,
    selectedOrderInvoiceNumbers,
    selectedOrderInvoiceUUIDs,
  ]);

  const onSharePaymentLinkMobile = useCallback(async () => {
    try {
      const response = await generatePaymentLink({
        variables: {
          creditMemoAmounts: selectedCreditMemoAmounts,
          orderInvoiceAmounts: selectedOrderInvoiceAmounts,
          restaurantUUID,
          supplierUUID,
        },
      });

      if (response.data?.generatePaymentLink) {
        await Share.share({
          message: response.data.generatePaymentLink.payment_link_url,
        });
      }
    } catch (e) {
      if (e instanceof ApolloError && getMessageFromGQLError(e)) {
        showErrorToast(getMessageFromGQLError(e));
      } else {
        showErrorToast();
      }
    }
  }, [
    generatePaymentLink,
    restaurantUUID,
    selectedCreditMemoAmounts,
    selectedOrderInvoiceAmounts,
    showErrorToast,
    supplierUUID,
  ]);

  const submitCheckPayment = useCallback(
    () =>
      submitCheckDeposit({
        chatUUID: chatUUIDs[0],
        creditMemos: selectedCreditMemoAmounts.map((cm) => ({
          amount_in_micros: cm.amount_in_micros,
          credit_memo_id: cm.credit_memo_id,
          credit_memo_number:
            allUnpaidItems.find((i) => i.uuid === cm.credit_memo_id)
              ?.displayNumber ?? '',
          restaurant_id: restaurantUUID,
        })),
        creditRequestBatchLineItems:
          partialPaymentBatchItems.length > 0
            ? adaptCreditRequestLineItemToBatchInput(partialPaymentBatchItems)
            : undefined,
        invoices: selectedOrderInvoiceAmounts.map((inv) => ({
          amount_in_micros: partialPaymentActive
            ? effectiveSubtotalInMicros
            : inv.amount_in_micros,
          invoice_id: inv.order_invoice_id,
          invoice_number:
            allUnpaidItems.find((i) => i.uuid === inv.order_invoice_id)
              ?.displayNumber ?? '',
          restaurant_id: restaurantUUID,
        })),
        memoNotes: memo ?? '',
      }),
    [
      allUnpaidItems,
      chatUUIDs,
      effectiveSubtotalInMicros,
      memo,
      partialPaymentActive,
      partialPaymentBatchItems,
      restaurantUUID,
      selectedCreditMemoAmounts,
      selectedOrderInvoiceAmounts,
      submitCheckDeposit,
    ],
  );

  const handleReviewSubmit = useCallback(async () => {
    if (checkSelected && checkCredentials) {
      try {
        const result = await submitCheckPayment();
        if (result.ok) {
          const store = usePaymentFlowStore.getState();
          store.reset();
          usePaymentMethodStore.getState().reset();
          resetCheckCaptureStore();
          clearKlarnaSelection();
          creditRequestStoreApi.getState().clearBatchItems();
          setShowPaymentSuccessModal(true);
          resetContext();
          setShowPaymentReviewActionSheet(false);
          return;
        }
        if (result.recovery === CheckSubmitRecovery.START_OVER) {
          showErrorToast(i18n.t('check_capture.submit_failed'));
          resetCheckCaptureStore();
          setShowPaymentReviewActionSheet(false);
        } else if (result.recovery === CheckSubmitRecovery.RETRY) {
          showErrorToast(result.message);
          resetCheckCaptureStore();
          setShowPaymentReviewActionSheet(false);
        } else {
          showErrorToast(result.message);
        }
      } catch (e) {
        showErrorToast();
      }
      return;
    }
    try {
      const result = await submitPayment();
      if (result?.alert_level === AlertLevel.Negative) {
        showErrorToast(result.alert_message!);
        refreshQueries();
      } else if (result?.alert_level === AlertLevel.Positive) {
        const store = usePaymentFlowStore.getState();
        store.reset();
        usePaymentMethodStore.getState().reset();
        resetCheckCaptureStore();
        clearKlarnaSelection();
        creditRequestStoreApi.getState().clearBatchItems();
        setShowPaymentSuccessModal(true);
        resetContext();
      }
    } catch (e) {
      showErrorToast();
    } finally {
      setShowPaymentReviewActionSheet(false);
    }
  }, [
    checkCredentials,
    checkSelected,
    clearKlarnaSelection,
    creditRequestStoreApi,
    refreshQueries,
    resetCheckCaptureStore,
    resetContext,
    showErrorToast,
    submitCheckPayment,
    submitPayment,
  ]);

  const onSharePaymentLinkWeb = useCallback(async () => {
    try {
      const response = await generatePaymentLink({
        variables: {
          creditMemoAmounts: selectedCreditMemoAmounts,
          orderInvoiceAmounts: selectedOrderInvoiceAmounts,
          restaurantUUID,
          supplierUUID,
        },
      });

      if (response.data?.generatePaymentLink) {
        Clipboard.setStringAsync(
          response.data.generatePaymentLink.payment_link_url,
        );
        showToast({
          content: i18n.t('payment_links.link_copied'),
        });
      }
    } catch (e) {
      if (e instanceof ApolloError && getMessageFromGQLError(e)) {
        showErrorToast(getMessageFromGQLError(e));
      } else {
        showErrorToast();
      }
    }
  }, [
    generatePaymentLink,
    restaurantUUID,
    selectedCreditMemoAmounts,
    selectedOrderInvoiceAmounts,
    showErrorToast,
    showToast,
    supplierUUID,
  ]);

  const isAllUnpaidItemsSelected =
    allUnpaidItems.length > 0 &&
    selectedInvoiceUUIDs.size === allUnpaidItems.length;

  const handlePartialPaymentChange = useCallback(
    (amountInMicros: number) => {
      const store = usePaymentFlowStore.getState();
      store.updatePartialAmountFlow({ amountInMicros, subtotalInMicros });
      setPaymentContextState({ autoShowModal: true });
      proceedToPay();
    },
    [proceedToPay, setPaymentContextState, subtotalInMicros],
  );

  const handlePartialPaymentClear = useCallback(() => {
    const store = usePaymentFlowStore.getState();
    store.updatePartialAmountFlow({
      amountInMicros: null,
      subtotalInMicros,
    });
    creditRequestStoreApi.getState().clearBatchItems();
  }, [creditRequestStoreApi, subtotalInMicros]);

  const selectedInvoiceNumber = useMemo(() => {
    if (!singleInvoiceSelected) {
      return '';
    }
    const [uuid] = Array.from(selectedInvoiceUUIDs);
    const item = allUnpaidItems.find(
      (entry) => entry.uuid === uuid && entry.type === PaymentItemType.Invoice,
    );
    return item?.displayNumber ?? '';
  }, [allUnpaidItems, selectedInvoiceUUIDs, singleInvoiceSelected]);

  const handlePayPress = useCallback(() => {
    const store = usePaymentFlowStore.getState();
    store.startFullPayFlow({ amountInMicros: subtotalInMicros });
    proceedToPay();
  }, [proceedToPay, subtotalInMicros]);

  const handlePayPartialPress = useCallback(() => {
    const store = usePaymentFlowStore.getState();
    if (isSmallLayout && showCreditRequest) {
      store.startCreditRequestFlow({ subtotalInMicros });
    } else {
      store.startPartialAmountFlow({ subtotalInMicros });
    }
    if (!isSmallLayout) {
      setInitialActionSheetView(
        capability.canShortPay && singleInvoiceSelected && showCreditRequest
          ? PaymentsActionSheetView.CreditRequestItems
          : PaymentsActionSheetView.PartialPayment,
      );
      setShowPaymentReviewActionSheet(true);
      return;
    }
    if (showCreditRequest) {
      navigate(APP_CONTAINER_ROUTES.ShortPayCreditRequestItems, {
        params: {
          onComplete: handleCreditRequestWorkflowComplete,
          orderInvoiceUUID: selectedOrderInvoices.uuids[0],
          subtotalInMicros,
          title: selectedInvoiceNumber
            ? i18n.t('invoice_number', { number: selectedInvoiceNumber })
            : i18n.t('credit_request.select_items'),
        },
        screen: SHORT_PAY_CREDIT_REQUEST_ROUTES.Items,
      });
    } else {
      navigate(APP_MODAL_ROUTES.PartialPayment, {
        currencyCode,
        initialAmountInMicros: partialPaymentAmountInMicros,
        invoiceNumber: selectedInvoiceNumber,
        invoiceTotalInMicros: subtotalInMicros,
        onClear: handlePartialPaymentClear,
        onSave: handlePartialPaymentChange,
      });
    }
  }, [
    capability.canShortPay,
    currencyCode,
    handleCreditRequestWorkflowComplete,
    handlePartialPaymentChange,
    handlePartialPaymentClear,
    isSmallLayout,
    navigate,
    partialPaymentAmountInMicros,
    selectedInvoiceNumber,
    selectedOrderInvoices.uuids,
    showCreditRequest,
    singleInvoiceSelected,
    subtotalInMicros,
  ]);

  return (
    <>
      {/* Enforces using RefreshControl on refetch for only small screens */}
      {loading && isSmallLayout !== refreshing ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator />
        </View>
      ) : !loading && allUnpaidItems.length === 0 && isOfflineModeActive ? (
        isSmallLayout ? (
          <OfflineComponent />
        ) : null
      ) : allUnpaidItems.length > 0 ? (
        <View style={styles.base}>
          {showSelectAllCheckbox ? (
            <Checkbox
              isSelected={isAllUnpaidItemsSelected}
              label={
                isAllUnpaidItemsSelected
                  ? i18n.t('deselect_all')
                  : i18n.t('select_all')
              }
              labelStyle={styles.selectAllLabel}
              onSelect={() => {
                usePaymentFlowStore.getState().reset();
                usePaymentMethodStore.getState().reset();
                if (isAllUnpaidItemsSelected) {
                  deselectAllUnpaidItems();
                } else {
                  selectAllUnpaidItems();
                }
              }}
              style={[
                styles.multiSelectingCheckbox,
                !isSmallLayout && styles.multiSelectingCheckboxDesktop,
              ]}
              trackingData={
                isAllUnpaidItemsSelected
                  ? { name: 'payments.deselect_all_unpaid' }
                  : { name: 'payments.select_all_unpaid' }
              }
            />
          ) : null}
          <UnpaidInvoiceList
            listSeparatorSpacing={contentSpacing}
            renderListItem={(unpaidItem) =>
              renderListItem({
                isChecked: selectedInvoiceUUIDs.has(unpaidItem.uuid),
                onCheck: () => toggleInvoiceSelection(unpaidItem.uuid),
                unpaidItem,
              })
            }
          />
          {(showPaymentLinksButton ||
            showPayInvoicesButton ||
            showPayPartialButton) &&
          currencyCode ? (
            <View
              style={[
                styles.buttonContainer,
                { paddingHorizontal: theme.sk.spacing[contentSpacing] },
              ]}
            >
              {showPaymentLinksButton ? (
                <Button
                  accessibilityLabel={i18n.t(
                    Platform.OS === 'web'
                      ? 'payment_links.copy_payment_link'
                      : 'payment_links.share_payment_link',
                  )}
                  containerStyle={styles.secondaryButton}
                  disabled={subtotalInMicros < 0}
                  loading={isGeneratingPaymentLink}
                  onPress={
                    Platform.OS === 'web'
                      ? onSharePaymentLinkWeb
                      : onSharePaymentLinkMobile
                  }
                  trackingData={{ name: 'payment_links.share_payment_link' }}
                  type={showPayInvoicesButton ? 'secondary' : undefined}
                >
                  {showPayInvoicesButton
                    ? i18n.t(
                        Platform.OS === 'web'
                          ? 'payment_links.copy_link'
                          : 'payment_links.share_link',
                      )
                    : i18n.t(
                        Platform.OS === 'web'
                          ? 'payment_links.copy_payment_link_total'
                          : 'payment_links.share_payment_link_total',
                        {
                          paymentAmount: formatCurrency(
                            subtotalInMicros,
                            currencyCode,
                          ),
                        },
                      )}
                </Button>
              ) : null}
              {showPayPartialButton && !showPayActionsButton ? (
                <Button
                  containerStyle={styles.secondaryButton}
                  disabled={subtotalInMicros < 0}
                  onPress={handlePayPartialPress}
                  textProps={{ numberOfLines: 1 }}
                  trackingData={{ name: 'payments.pay_partial_button' }}
                  type="secondary"
                >
                  {i18n.t('partial_pay')}
                </Button>
              ) : null}
              {showPayInvoicesButton && showPayActionsButton ? (
                <PayActionsButton
                  containerStyle={styles.primaryButton}
                  currencyCode={currencyCode}
                  // Disable if the subtotal is negative due to credits
                  disabled={subtotalInMicros < 0}
                  onPay={ftniEnabled ? onHandleInitiateFTNI : handlePayPress}
                  onShortPay={handlePayPartialPress}
                  subtotalInMicros={subtotalInMicros}
                />
              ) : showPayInvoicesButton ? (
                <Button
                  containerStyle={styles.primaryButton}
                  // Disable if the subtotal is negative due to credits
                  disabled={subtotalInMicros < 0}
                  onPress={ftniEnabled ? onHandleInitiateFTNI : handlePayPress}
                  testID="pay-invoices-button"
                  textProps={{ numberOfLines: 1 }}
                  trackingData={{ name: 'payments.pay_invoices_button' }}
                >
                  {showPaymentLinksButton || showPayPartialButton
                    ? i18n.t('pay_amount_no_separator', {
                        paymentAmount: formatCurrency(
                          subtotalInMicros,
                          currencyCode,
                        ),
                      })
                    : i18n.t(
                        allUnpaidItemsSelected ? 'pay_all' : 'pay_selected',
                        {
                          paymentAmount: formatCurrency(
                            subtotalInMicros,
                            currencyCode,
                          ),
                        },
                      )}
                </Button>
              ) : null}
            </View>
          ) : null}
          {showPayInvoicesButton ? (
            <>
              {showSmallPaymentReviewActionSheet ? (
                <PaymentReviewActionSheet
                  calculatedTransactionFee={effectiveCalculatedTransactionFee}
                  checkNumber={checkNumber}
                  isCash={cashActive}
                  isKlarnaBusy={isKlarnaBusy}
                  isManualCheck={manualCheckActive}
                  isSubmitting={isSubmittingPayment}
                  onChange={() => {
                    setPaymentContextState({ autoShowModal: true });
                    setShowPaymentReviewActionSheet(false);
                    navigate(PAYMENT_ROUTES.PaymentMethod, {
                      isDistributorUser: capability.isDistributorUser,
                      klarnaPaymentMethods,
                      onAddUnsavedPaymentMethod: (paymentJson) => {
                        clearCheckSelection();
                        clearKlarnaSelection();
                        navigate(PAYMENT_ROUTES.Base, {
                          unsavedPaymentMethodJson: paymentJson,
                        });
                      },
                      onAfterSavePaymentMethod: () => {
                        clearCheckSelection();
                        clearKlarnaSelection();
                        // Allows review action sheet to open in UnpaidInvoiceComponent
                        setPaymentContextState({ autoShowModal: true });
                      },
                      onPayByCheck: capability.canPayByCheck
                        ? onPayByCheck
                        : undefined,
                      onRecordCashPayment: capability.canRecordCashPayment
                        ? onRecordCashPayment
                        : undefined,
                      onRecordCheckPayment: capability.canRecordCheckPayment
                        ? onRecordCheckPayment
                        : undefined,
                      onSelectKlarna: klarnaPaymentMethods.length
                        ? onSelectKlarna
                        : undefined,
                      onSelectPaymentMethod: (
                        paymentMethod?: PaymentMethodOption,
                      ) => {
                        clearCheckSelection();
                        clearKlarnaSelection();
                        setPaymentContextState({
                          selectedPaymentMethod: paymentMethod,
                        });
                      },
                    });
                  }}
                  onChangeCheckNumber={setCheckNumber}
                  onClose={() => {
                    setShowPaymentReviewActionSheet(false);
                    resetShortPay();
                    resetCheckCaptureStore();
                    clearKlarnaSelection();
                    setPaymentContextState({ autoShowModal: false });
                  }}
                  onCollectPADConsent={() => {
                    setPaymentContextState({ autoShowModal: false });
                    setShowPaymentReviewActionSheet(false);
                    navigate(APP_MODAL_ROUTES.CollectPADConsent, {
                      onAfterPADConsent: handleReviewSubmit,
                    });
                  }}
                  onKlarnaContinue={
                    klarnaPaymentMethods.length ? onKlarnaContinue : undefined
                  }
                  onSubmit={handleReviewSubmit}
                  payAmountInMicrosOverride={payAmountInMicrosOverride}
                  reviewBanner={reviewBanner?.({
                    jumpToTabCardApplication: () => {
                      setPaymentContextState({ autoShowModal: true });
                      setShowPaymentReviewActionSheet(false);
                      onReviewBannerPress?.();
                    },
                  })}
                  subtotalInMicros={effectiveSubtotalInMicros}
                  transactionFeeName={transactionFeeName}
                />
              ) : null}
            </>
          ) : null}
        </View>
      ) : (
        <>
          <EmptyStateMinimal
            children={
              <Text style={[TYPOGRAPHY.medium, styles.emptyStateText]}>
                {i18n.t('payments.unpaid_empty_message')}
              </Text>
            }
            containerStyle={styles.emptyStateContainer}
            ImageComponent={CarrotIcon}
            text={i18n.t('payments.unpaid_empty_title')}
            textStyle={TYPOGRAPHY.h2}
          />
        </>
      )}
      {showPaymentSuccessModal ? (
        <PaymentSuccessActionSheet
          onClose={() => {
            setShowPaymentSuccessModal(false);
            refreshQueries();
          }}
          onPaymentSuccessAutoPayEnrollment={onPaymentSuccessAutoPayEnrollment}
        />
      ) : null}
      {showPaymentReviewActionSheet && !isSmallLayout ? (
        <CombinedPaymentsActionSheet
          calculatedTransactionFee={effectiveCalculatedTransactionFee}
          checkNumber={checkNumber}
          creditRequestEnabled={
            capability.canShortPay && singleInvoiceSelected && showCreditRequest
          }
          effectiveSubtotalInMicros={effectiveSubtotalInMicros}
          initialView={initialActionSheetView}
          isCash={cashActive}
          isDistributorUser={capability.isDistributorUser}
          isKlarnaBusy={isKlarnaBusy}
          isManualCheck={manualCheckActive}
          isSubmitting={isSubmittingPayment}
          klarnaPaymentMethods={klarnaPaymentMethods}
          onChangeCheckNumber={setCheckNumber}
          onClose={() => {
            setShowPaymentReviewActionSheet(false);
            resetShortPay();
            resetCheckCaptureStore();
            clearKlarnaSelection();
            setPaymentContextState({ autoShowModal: false });
          }}
          onCreditRequestWorkflowComplete={
            capability.canShortPay && singleInvoiceSelected
              ? handleCreditRequestWorkflowComplete
              : undefined
          }
          onKlarnaContinue={
            klarnaPaymentMethods.length ? onKlarnaContinue : undefined
          }
          onPartialPaymentChange={
            capability.canShortPay && singleInvoiceSelected
              ? handlePartialPaymentChange
              : undefined
          }
          onPartialPaymentClear={
            capability.canShortPay && singleInvoiceSelected
              ? handlePartialPaymentClear
              : undefined
          }
          onPaymentSuccess={() => {
            const store = usePaymentFlowStore.getState();
            store.reset();
            usePaymentMethodStore.getState().reset();
            resetCheckCaptureStore();
            creditRequestStoreApi.getState().clearBatchItems();
          }}
          onPaymentSuccessAutoPayEnrollment={onPaymentSuccessAutoPayEnrollment}
          onRecordCashPayment={
            capability.canRecordCashPayment ? markCashPayment : undefined
          }
          onRecordCheckPayment={
            capability.canRecordCheckPayment ? markCheckPayment : undefined
          }
          partialPaymentAmountInMicros={partialPaymentAmountInMicros}
          payAmountInMicrosOverride={payAmountInMicrosOverride}
          refreshQueries={refreshQueries}
          reviewBanner={reviewBanner}
          selectedOrderInvoiceUUID={
            singleInvoiceSelected ? selectedOrderInvoices.uuids[0] : undefined
          }
          submitCheckPayment={submitCheckPayment}
          submitPayment={submitPayment}
          subtotalInMicros={subtotalInMicros}
          tabCardDetails={tabCardDetails}
          transactionFeeName={transactionFeeName}
          walletFooterBanner={walletFooterBanner}
        />
      ) : null}
    </>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    flex: 1,
  },
  buttonContainer: {
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
    paddingVertical: theme.sk.spacing.spaceMedium,
  },
  emptyStateContainer: {
    backgroundColor: theme.sk.color.bg.primary.default,
  },
  emptyStateText: {
    lineHeight: 24,
    marginTop: theme.sk.spacing.spaceSMedium,
    textAlign: 'center',
  },
  loadingContainer: {
    marginVertical: theme.sk.spacing.spaceMedium,
  },
  multiSelectingCheckbox: {
    backgroundColor: 'transparent',
    borderBottomColor: theme.sk.color.border.primary,
    borderBottomWidth: 1,
    height: theme.sk.spacing.space3xlarge,
    paddingHorizontal: theme.sk.spacing.spaceMedium,
    paddingVertical: 0,
  },
  multiSelectingCheckboxDesktop: {
    paddingHorizontal: theme.sk.spacing.spaceLarge,
  },
  primaryButton: {
    flex: 1.5,
  },
  secondaryButton: {
    flex: 1,
  },
  selectAllLabel: {
    ...FONTS.primarySemiBold,
    color: theme.sk.color.text.primary,
    fontSize: theme.fontSizes.small,
    marginHorizontal: theme.sk.spacing.spaceSMedium,
  },
}));
